import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CopyCode } from "@/components/CopyCode";
import { Link } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar } from "recharts";
import { api } from "@/lib/api";
import { KpiCard } from "@/components/kpi/KpiCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { formatBs, formatUsd, formatCop, formatDate } from "@/lib/format";
import { Ticket, Coins, Trophy, TrendingUp, PlusCircle, Store, RefreshCw, ShieldAlert, LogIn, XCircle, Lock, KeyRound } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { toast } from "sonner";

// Gavetas nativas: cada moneda en su monto original, sin conversiones.
const nativeMoney = (v, cur) => (cur === "USD" ? `$ ${formatUsd(v)}` : cur === "COP" ? `COP ${formatCop(v)}` : `Bs ${formatBs(v)}`);
const GAVETAS = [["bs", "BS", "text-primary"], ["usd", "USD", "text-sky-300"], ["cop", "COP", "text-rose-300"]];


const TONE_STYLES = {
  slate: "border-border/70 text-slate-200",
  sky:   "border-sky-500/30 text-sky-300",
  amber: "border-amber-500/30 text-amber-300",
  rose:  "border-rose-500/30 text-rose-300",
};

function SecurityMini({ label, value, icon: Icon, tone = "slate", testid }) {
  const cls = TONE_STYLES[tone] || TONE_STYLES.slate;
  return (
    <Card className={`bg-card p-3 flex items-center gap-3 ${cls}`} data-testid={testid}>
      <div className={`h-9 w-9 rounded-lg grid place-items-center border ${cls} bg-secondary/40`}>
        <Icon size={16} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground truncate">{label}</div>
        <div className="text-xl font-display font-semibold tabular-nums leading-tight">{value}</div>
      </div>
    </Card>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: summary } = useQuery({
    queryKey: ["reports", "summary", 7],
    queryFn: async () => (await api.get("/reports/summary?days=7")).data,
  });
  const { data: byDay = [] } = useQuery({
    queryKey: ["reports", "by-day", 14],
    queryFn: async () => (await api.get("/reports/by-day?days=14")).data,
  });
  const { data: byTaquilla = [] } = useQuery({
    queryKey: ["reports", "by-taquilla", 7],
    queryFn: async () => (await api.get("/reports/by-taquilla?days=7")).data,
  });
  const { data: recentTickets = [] } = useQuery({
    queryKey: ["tickets", "recent"],
    queryFn: async () => (await api.get("/tickets?limit=8")).data,
  });
  const { data: rate } = useQuery({
    queryKey: ["rate", "current"],
    queryFn: async () => (await api.get("/exchange-rates/current")).data,
  });
  const syncRate = useMutation({
    mutationFn: async () => (await api.post("/exchange-rates/sync?force_refresh=true")).data,
    onSuccess: (r) => {
      toast.success(`Tasa BCV actualizada: ${formatBs(r.bs_per_usd)} Bs/USD`);
      qc.invalidateQueries({ queryKey: ["rate", "current"] });
    },
    onError: (e) => {
      const detail = e?.response?.data?.detail || e?.message || "Error";
      toast.error(`No se pudo actualizar la tasa: ${detail}`);
    },
  });

  const canSeeSecurity = user?.role === "admin" || user?.role === "banca_owner";
  const { data: sec } = useQuery({
    queryKey: ["audit", "security-health", 24],
    queryFn: async () => (await api.get("/audit-log/security-health?hours=24")).data,
    enabled: canSeeSecurity,
    refetchInterval: 60_000,
  });

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground">Resumen últimos 7 días.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {user?.role === "admin" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => syncRate.mutate()}
              disabled={syncRate.isPending}
              data-testid="dashboard-refresh-bcv-button"
              title="Fuerza consulta viva al proveedor BCV (bypassa cache de 30 min)"
            >
              <RefreshCw size={14} className={`mr-1.5 ${syncRate.isPending ? "animate-spin" : ""}`} />
              {syncRate.isPending ? "Actualizando…" : "Actualizar Tasa BCV"}
            </Button>
          )}
          <Link to="/admin/taquillas"><Button variant="outline" size="sm" data-testid="dashboard-quick-action-create-taquilla"><Store size={16} className="mr-1.5"/>Nueva Taquilla</Button></Link>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        {GAVETAS.map(([k, cur, accent]) => (
          <KpiCard
            key={k}
            label={`Ventas (${cur})`}
            value={nativeMoney(summary?.[k]?.venta_neta, cur)}
            meta={`${summary?.[k]?.tickets ?? 0} tickets · Utilidad ${nativeMoney(summary?.[k]?.profit, cur)}`}
            testid={`kpi-sales-${k}`}
            accent={accent}
          />
        ))}
        <KpiCard
          label="Premios pagados"
          value={nativeMoney(summary?.bs?.pagado, "BS")}
          meta={`${nativeMoney(summary?.usd?.pagado, "USD")} · ${nativeMoney(summary?.cop?.pagado, "COP")} · ${summary?.winners ?? 0} ganadores`}
          testid="kpi-payouts"
          accent="text-[#2ed4c7] dark:[#102747]"
        />
      </div>
      {rate && <p className="text-xs text-muted-foreground -mt-3" data-testid="dashboard-bcv-rate">Tasa BCV {formatBs(rate.bs_per_usd)} · cada gaveta suma solo los tickets emitidos en su moneda.</p>}

      {canSeeSecurity && (
        <div data-testid="security-health-panel" className="space-y-2">
          <div className="flex items-center gap-2">
            <ShieldAlert size={16} className="text-primary" />
            <div className="font-display font-semibold text-sm">Salud de seguridad</div>
            <span className="text-xs text-muted-foreground">últimas 24 h</span>
            <Link to="/admin/auditoria" className="ml-auto text-xs text-primary hover:underline" data-testid="security-health-see-all">
              Ver auditoría →
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <SecurityMini
              label="Logins fallidos"
              value={sec?.login_fail ?? 0}
              icon={XCircle}
              tone={(sec?.login_fail ?? 0) > 20 ? "rose" : (sec?.login_fail ?? 0) > 5 ? "amber" : "slate"}
              testid="security-kpi-login-fail"
            />
            <SecurityMini
              label="Cuentas bloqueadas"
              value={sec?.login_locked ?? 0}
              icon={Lock}
              tone={(sec?.login_locked ?? 0) > 0 ? "rose" : "slate"}
              testid="security-kpi-login-locked"
            />
            <SecurityMini
              label="Tickets anulados"
              value={sec?.ticket_void ?? 0}
              icon={ShieldAlert}
              tone={(sec?.ticket_void ?? 0) > 30 ? "amber" : "slate"}
              testid="security-kpi-ticket-void"
            />
            <SecurityMini
              label="Tokens revocados"
              value={sec?.revoked_tokens ?? 0}
              icon={KeyRound}
              tone="sky"
              testid="security-kpi-revoked-tokens"
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Card className="lg:col-span-8 p-4 bg-card border-border" data-testid="chart-sales-by-day">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp size={16} className="text-primary" />
            <div className="font-display font-semibold">Ventas por día</div>
            <span className="ml-auto text-xs text-muted-foreground">Solo tickets en Bs · últimos 14 días</span>
          </div>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height={256} minWidth={0}>
              <LineChart data={byDay} margin={{ top: 6, right: 8, left: 4, bottom: 0 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} width={60} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, color: "hsl(var(--foreground))" }} labelStyle={{ color: "hsl(var(--foreground))" }} />
                <Line type="monotone" dataKey="total_bs" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="lg:col-span-4 p-4 bg-card border-border" data-testid="chart-sales-by-taquilla">
          <div className="flex items-center gap-2 mb-3">
            <Store size={16} className="text-primary" />
            <div className="font-display font-semibold">Top taquillas</div>
          </div>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height={256} minWidth={0}>
              <BarChart data={byTaquilla.slice(0, 6)} layout="vertical" margin={{ left: 8, right: 8 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" horizontal={false} />
                <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} width={100} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, color: "hsl(var(--foreground))" }} />
                <Bar dataKey="total_bs" fill="hsl(var(--chart-3))" radius={[4,4,4,4]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card className="p-4 bg-card border-border">
        <div className="flex items-center gap-2 mb-3">
          <Ticket size={16} className="text-primary" />
          <div className="font-display font-semibold">Tickets recientes</div>
          <span className="ml-auto text-xs text-muted-foreground">últimos 8</span>
        </div>
        <Separator className="mb-3" />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-muted-foreground text-left">
                <th className="py-2 pr-3">Código</th>
                <th className="py-2 pr-3">Fecha</th>
                <th className="py-2 pr-3">Estado</th>
                <th className="py-2 pr-3">Moneda</th>
                <th className="py-2 pr-3 text-right">Monto</th>
                <th className="py-2 pr-3 text-right">Premio</th>
              </tr>
            </thead>
            <tbody>
              {recentTickets.length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Sin tickets aún.</td></tr>
              )}
              {recentTickets.map((t) => (
                <tr key={t.id} className="border-t border-border/60">
                  <td className="py-2 pr-3 font-mono text-primary"><CopyCode code={t.code} /></td>
                  <td className="py-2 pr-3">{formatDate(t.created_at)}</td>
                  <td className="py-2 pr-3">
                    <StatusBadge status={t.status} />
                  </td>
                  <td className="py-2 pr-3 text-xs font-semibold">{t.currency || "BS"}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{nativeMoney(t[`total_${(t.currency || "BS").toLowerCase()}`], t.currency || "BS")}</td>
                  <td className="py-2 pr-3 text-right tabular-nums text-amber-300">{nativeMoney(t[`payout_total_${(t.currency || "BS").toLowerCase()}`], t.currency || "BS")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: "bg-sky-500/15 text-sky-300 border-sky-500/30",
    winner: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
    paid: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    lost: "bg-slate-500/15 text-slate-300 border-slate-500/30",
    void: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  };
  const label = {
    active: "Activo",
    winner: "Ganador",
    paid: "Pagado",
    lost: "Sin premio",
    void: "Anulado",
  }[status] || status;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${map[status] || ""}`}>{label}</span>;
}

export { StatusBadge };
